import _ from 'lodash';

import commonFetch, { get, post } from './commonFetch';
import {
  TravelerProfile,
  TravelerProfileUpdateRequest,
  ContactPoint,
  MessageType,
  TravelerProfileSearchCriteria,
} from '../types/travelerProfile';

import { TravelerChangePasswordValues } from '../types/travelerProfileForm';
import { ChangePasswordValues } from '../types/auth';

import {
  Clock,
  ContactPointsToUIAlertTypeMapping,
  ContactPointsToAPIMessageFormatMapping,
  ContactPointsToAPIAlertTypeMapping,
  ContactPointsMessageType,
  ContactPointsAPIAlertType,
} from '../traveler/profile/TravelerProfileConstants';

export type APIAlertType = keyof typeof ContactPointsAPIAlertType;

interface ContactPointAPI {
  alertType: APIAlertType;
  messageType: MessageType;
  contact?: string;
  format?: string;
  isDisabled: boolean;
  phoneNumberE164?: string;
  countryCode?: string;
  isActive: boolean;
}

interface ApiTravelerProfile {
  travelerKey: string;
  firstName: string;
  lastName: string;
  email: string;
  accountId: number;
  timeFormat: number;
  contactPoints: ContactPointAPI[];
}

export interface ApiTravelerProfileUpdateRequest {
  firstName: string;
  lastName: string;
  email: string;
  timeFormat: number;
  contactPoints: ContactPointAPI[];
}

const transformToContactPoint = (
  contactPoint: ContactPointAPI,
): ContactPoint => {
  return {
    alertType:
      ContactPointsToUIAlertTypeMapping[contactPoint.alertType.toUpperCase() as keyof typeof ContactPointsToUIAlertTypeMapping],
    messageType: contactPoint.messageType,
    contact: contactPoint.contact,
    format: contactPoint.format,
    phoneNumberE164: contactPoint.phoneNumberE164,
    countryCode: contactPoint.countryCode,
    isDisabled: contactPoint.isDisabled,
  };
};

const transformToTravelerProfile = (
  apiTravelerProfile: ApiTravelerProfile,
): TravelerProfile => {
  const mappedContactPoints = _.map(
    apiTravelerProfile.contactPoints,
    transformToContactPoint,
  );
  return {
    travelerKey: apiTravelerProfile.travelerKey,
    firstName: apiTravelerProfile.firstName,
    lastName: apiTravelerProfile.lastName,
    email: apiTravelerProfile.email,
    accountId: apiTravelerProfile.accountId.toString(),
    clock:
      apiTravelerProfile.timeFormat === 0
        ? Clock.TWELVE_HOUR
        : Clock.TWENTY_FOUR_HOUR,
    contactPoints: mappedContactPoints,
  };
};

const transformToApiContactPoint = (
  contactPoint: ContactPoint,
): ContactPointAPI => {
  return {
    alertType: ContactPointsToAPIAlertTypeMapping[contactPoint.alertType],
    messageType: contactPoint.messageType,
    contact: contactPoint.contact,
    format: ContactPointsToAPIMessageFormatMapping[contactPoint.messageType],
    phoneNumberE164: contactPoint.phoneNumberE164,
    countryCode: contactPoint.countryCode,
    isDisabled: contactPoint.isDisabled || false,
    isActive: true,
  };
};

const transformToApiTravelerProfileUpdateRequest = (
  travelerProfileUpdateRequest: TravelerProfileUpdateRequest,
): ApiTravelerProfileUpdateRequest => {
  const mappedContactPoints = _.map(
    travelerProfileUpdateRequest.contactPoints,
    transformToApiContactPoint,
  );
  return {
    firstName: travelerProfileUpdateRequest.firstName,
    lastName: travelerProfileUpdateRequest.lastName,
    email: travelerProfileUpdateRequest.email,
    timeFormat:
      travelerProfileUpdateRequest.clock === Clock.TWELVE_HOUR ? 0 : 1,
    contactPoints: mappedContactPoints,
  };
};

const transformToApiTravelerPasswordInfo = (
  changePasswordInfo: TravelerChangePasswordValues,
): ChangePasswordValues => {
  return {
    current: changePasswordInfo.currentPassword,
    password: changePasswordInfo.newPassword,
    confirm: changePasswordInfo.confirmPassword,
  };
};

export const getTravelerProfile = (
  includeHealthAlerts: boolean,
  travelerKey?: string,
): Promise<TravelerProfile> => {
  return get(
    travelerKey ? `/api/trips/agent/traveler/${travelerKey}` : '/api/traveler',
  ).then(apiTravelerProfile => {
    return getTransformedTravelerProfileWithActiveContactPoints(
      apiTravelerProfile as ApiTravelerProfile,
      includeHealthAlerts,
    );
  });
};

const getDisplayedAlertTypes = (
  includeHealth: boolean,
): Array<keyof typeof ContactPointsAPIAlertType> => {
  const displayedAlertTypes: Array<keyof typeof ContactPointsAPIAlertType> = [
    'HOME',
    'AWAY',
  ];
  if (includeHealth) {
    displayedAlertTypes.push('HEALTH');
  }
  return displayedAlertTypes;
};

const healthContactPoint = (email: string) => ({
  alertType: ContactPointsAPIAlertType.HEALTH,
  messageType: ContactPointsMessageType.EMAIL,
  contact: email,
  isDisabled: true,
  isActive: true,
});

const getTransformedTravelerProfileWithActiveContactPoints = (
  apiTravelerProfile: ApiTravelerProfile,
  includeHealthAlerts: boolean,
): TravelerProfile => {
  const displayedAlertTypes = getDisplayedAlertTypes(includeHealthAlerts);
  const activeContactPoints = apiTravelerProfile.contactPoints
    .filter(contactPoint => contactPoint.isActive)
    .filter(contactPoint =>
      displayedAlertTypes.includes(contactPoint.alertType.toUpperCase() as keyof typeof ContactPointsAPIAlertType),
    );
  if (
    includeHealthAlerts &&
    !activeContactPoints.some(
      contactPoint =>
        contactPoint.alertType.toUpperCase() ==
        ContactPointsAPIAlertType.HEALTH,
    )
  ) {
    activeContactPoints.push(
      healthContactPoint(apiTravelerProfile.travelerKey),
    );
  }
  const travelerProfileWithActiveContactPoints = {
    ...apiTravelerProfile,
    contactPoints: activeContactPoints,
  };
  return transformToTravelerProfile(travelerProfileWithActiveContactPoints);
};

export const getTravelerProfiles = (
  searchCriteria: TravelerProfileSearchCriteria,
  includeHealthAlerts: boolean,
): Promise<Array<TravelerProfile>> => {
  const cleanedSearchCriteria = _.pickBy(
    searchCriteria,
    value => !_.isEmpty(value),
  );
  return get('/api/agents/travelers', cleanedSearchCriteria).then((result: unknown) => {
    const typedResult = result as { data?: { travelerProfiles: ApiTravelerProfile[] } };
    if (typedResult?.data) {
      return typedResult.data.travelerProfiles.map((profile: ApiTravelerProfile) =>
        getTransformedTravelerProfileWithActiveContactPoints(
          profile,
          includeHealthAlerts,
        ),
      );
    } else {
      return [];
    }
  });
};

export const putTravelerProfile = (
  travelerProfileUpdateRequest: TravelerProfileUpdateRequest,
  travelerKey: string,
  isAgent: boolean,
): Promise<void> => {
  const apiTravelerProfileUpdateRequest = transformToApiTravelerProfileUpdateRequest(
    travelerProfileUpdateRequest,
  );
  return commonFetch(
    isAgent ? `/api/trips/agent/traveler/${travelerKey}` : '/api/traveler',
    apiTravelerProfileUpdateRequest,
    'POST',
  ).then(response =>
    response.json().then(responseBody => {
      if (!response.ok) {
        const error = {
          errorBody: responseBody,
          statusCode: response.status,
        };
        throw error;
      }
      return responseBody;
    }),
  );
};

export const changeTravelerPassword = (
  changePasswordInfo: TravelerChangePasswordValues,
): Promise<void> => {
  return post(
    '/traveler/password',
    transformToApiTravelerPasswordInfo(changePasswordInfo),
  );
};

export const sendTestAlert = (contactPoint: ContactPoint): Promise<string> => {
  const recipient =
    contactPoint.messageType === ContactPointsMessageType.EMAIL
      ? contactPoint.contact
      : contactPoint.phoneNumberE164;

  return commonFetch(
    '/api/traveler/testAlerts',
    {
      messageType: contactPoint.messageType,
      recipient,
      recipientCountryCode: contactPoint.countryCode,
    },
    'POST',
  ).then(response =>
    response.json().then(responseBody => {
      if (!response.ok) {
        const error = responseBody;
        throw error;
      }
      return recipient || '';
    }),
  );
};